import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { TabsTypeList, TypeTab } from "@components/AdCampaignTabs";
import { ExpandableTable } from "@components/ExpandebleTable";
import { useKeywordStore } from "src/store/keyword.state";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";
import { CanvasApi } from "@services/canvas/canvas.api";
import styles from "./styles.module.scss";
import { formatData } from "./table.utils";
import {
  fetcherMap,
  getAllAvailableColumns,
  getColumns,
  getDefaultColumns,
  getSaturationKey,
  getSaturationKeyField,
  isTotalFirst,
  level1Map,
  level2Map,
  removeTotalChildren,
  toIsoDate,
  updateNestedRecord,
  updateRecordChildren,
} from "./utils";
import { INestedData, TableItem } from "./utils.types";
import { useAdsAnaliticsStore } from "src/store/ads-analitics.store";
import classNames from "classnames";
import { ColumnsSettingsModal } from "./ColumnSettingsModal";
import { useCanvaStore } from "src/store/canva/canva.state";
import { useIsOn } from "@hooks/useFeatureToggle";
import { FEATURE_TOGGLES } from "@lib/featureToggles";

interface CampaignsTableProps {
  isWhatIf?: boolean;
  customAsin?: string[];
  autoHeight?: boolean;
}

export const CampaignsTable = ({
  isWhatIf,
  customAsin,
  autoHeight,
}: CampaignsTableProps) => {
  const { keyword, setKeywords } = useKeywordStore();
  const { setCampaignsTab, setSaturationKeys: handleChangeSaturationKeys } =
    useCanvaStore();
  const { dateRange } = useDashboardStore();
  const { adSpend: saturation } = useAdsAnaliticsStore().adSlides;
  const [isLoading, setIsLoading] = useState(false);
  const canvasApi = useMemo(() => new CanvasApi(), []);

  const [temporaryData, setTemporaryData] = useState<TableItem[] | undefined>();
  const [tableData, setTableData] = useState<any[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [expandedRowsData, setExpandedRowsData] = useState<Record<string, any>>(
    {},
  );
  const [previousSaturation, setPreviousSaturation] = useState(saturation);
  const [saturationKeys, setSaturationKeys] = useState<{
    level1: string[];
    level2: string[];
    level3: string[];
  }>({
    level1: [],
    level2: [],
    level3: [],
  });
  const [tableKeys, setTableKeys] = useState<React.Key[]>([]);
  const [selectedTab, setSelectedTab] = useState<TypeTab>("Campaigns");
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    getDefaultColumns(selectedTab),
  );
  const adCampaignCampaignTableEnabled = useIsOn(
    FEATURE_TOGGLES.AD_CAMPAIGN_MANAGER_CAMPAIGN_TABLE,
  );

  const [tooltipTabsTitle, setTooltipTabsTitle] = useState("");
  const [tooltipKeysTitle, setTooltipKeysTitle] = useState("");

  const [asin, setAsin] = useState<string[]>(customAsin || []);

  const { startDate, endDate } = dateRange;
  const from = toIsoDate(startDate);
  const to = toIsoDate(endDate);

  const tableRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  // ---- Extracted: update selected nested records ----
  const handleUpdateSelectedNestedRecords = useCallback(async () => {
    const nestedSelectedRows = tableData.filter(
      (row) => tableKeys.includes(row.key) && row.level > 1,
    );
    if (nestedSelectedRows.length === 0) return;

    setIsLoading(true);
    try {
      const updates = nestedSelectedRows.map((row) => {
        const keys = saturationKeys[`level${row.level}`] || [];
        return updateNestedRecord(row, tableData, {
          from,
          to,
          selectedTab,
          saturation,
          asins: asin,
          saturationKeys: keys,
        });
      });
      const results = await Promise.all(updates);
      const updatedData = results
        .reduce(mergeResults, tableData)
        .filter((row) => Object.values(row).some((val) => val !== "Total"));
      setTemporaryData(updatedData);
    } catch (error) {
      console.error(
        "Error updating nested records on saturation change:",
        error,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    tableData,
    tableKeys,
    saturationKeys,
    from,
    to,
    selectedTab,
    saturation,
    asin,
  ]);

  // ---- Extracted: restore expanded rows ----
  const handleRestoreExpandedRows = useCallback(async () => {
    if (!temporaryData || expandedRowKeys.length === 0) return;
    for (const key of expandedRowKeys) {
      const record = findRecordByKey(temporaryData, key.toString());
      if (
        record &&
        !record.children?.length &&
        expandedRowsData[key.toString()]
      ) {
        setTemporaryData(
          (prev) =>
            prev &&
            updateRecordChildren(
              prev,
              key as number,
              expandedRowsData[key.toString()],
            ),
        );
      }
    }
  }, [temporaryData, expandedRowKeys, expandedRowsData]);

  // ---- Extracted: prepare table data ----
  const prepareTableData = useCallback(() => {
    let data: TableItem[] = temporaryData || [];
    if (isTotalFirst(temporaryData)) {
      data = [...(temporaryData || [])].reverse();
    }
    const finalData = formatData({
      data: removeTotalChildren(data) || [],
      openTooltip: setTooltipTabsTitle,
      openedTooltip: tooltipTabsTitle,
      keyTooltip: tooltipKeysTitle,
      setKeyTooltip: setTooltipKeysTitle,
      keyword,
      setKeywords,
      withKeywords: selectedTab === "Campaigns",
    });
    setTableData(finalData);
  }, [
    temporaryData,
    tooltipTabsTitle,
    tooltipKeysTitle,
    keyword,
    setKeywords,
    selectedTab,
  ]);

  const getFilteredColumns = useCallback(() => {
    const allColumns = getColumns(selectedTab, isWhatIf);
    const primaryColumnKey = getSaturationKeyField(selectedTab);

    const selectionColumn = allColumns.find((col) => col.key === "selection");
    const primaryColumn = allColumns.find(
      (col) => col.dataIndex === primaryColumnKey,
    );

    const filteredColumns = allColumns.filter(
      (col) =>
        selectedColumns.includes(col.key) &&
        col.dataIndex !== primaryColumnKey &&
        col.key !== "selection",
    );

    const result: typeof filteredColumns = [];
    if (selectionColumn) result.push(selectionColumn);
    if (primaryColumn) result.push(primaryColumn);
    result.push(...filteredColumns);
    return result;
  }, [selectedTab, isWhatIf, selectedColumns]);

  useEffect(() => {
    setCampaignsTab(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    handleChangeSaturationKeys(saturationKeys.level1);
  }, [saturationKeys.level1]);

  function initializeColumns() {
    try {
      let defaultCols = getDefaultColumns(selectedTab);
      const primaryColumnKey = getSaturationKeyField(selectedTab);
      if (!defaultCols.includes(primaryColumnKey)) {
        defaultCols.unshift(primaryColumnKey);
      }
      const allAvailableColumns = getAllAvailableColumns(selectedTab);
      const regularColumns = defaultCols.filter((colKey) => {
        const column = allAvailableColumns.find((col) => col.key === colKey);
        return column && !column.isWhatIf && colKey !== primaryColumnKey;
      });
      if (regularColumns.length < 5) {
        const additionalColumns = allAvailableColumns
          .filter(
            (col) =>
              !col.isWhatIf &&
              !defaultCols.includes(col.key) &&
              col.key !== primaryColumnKey,
          )
          .slice(0, 5 - regularColumns.length)
          .map((col) => col.key);
        defaultCols = [...defaultCols, ...additionalColumns];
      }
      if (isWhatIf) {
        const whatIfColumns = allAvailableColumns
          .filter((col) => col.isWhatIf)
          .slice(0, 4)
          .map((col) => col.key);
        whatIfColumns.forEach((key) => {
          if (!defaultCols.includes(key)) defaultCols.push(key);
        });
      } else {
        defaultCols = defaultCols.filter((colKey) => {
          const column = allAvailableColumns.find((col) => col.key === colKey);
          return !column?.isWhatIf;
        });
      }
      setSelectedColumns(defaultCols);
    } catch (error) {
      console.error("Error initializing columns:", error);
      setSelectedColumns([getSaturationKeyField(selectedTab)]);
    }
  }

  useEffect(initializeColumns, [selectedTab, isWhatIf]);

  useEffect(() => {
    if (tableRef.current) {
      setHeight(tableRef.current.offsetHeight);
    }
  }, [tableRef.current?.offsetHeight, tableData]);

  useEffect(() => {
    setAsin(customAsin || []);
  }, [customAsin]);

  /**
   * Fetch top-level data.
   */
  useEffect(() => {
    const fetchTableData = async () => {
      setIsLoading(true);
      try {
        const fetchMethod = fetcherMap[selectedTab];
        if (!fetchMethod) return;
        const fetchedData = await fetchMethod(
          from,
          to,
          saturation,
          asin,
          saturationKeys.level1,
        );
        const shapedData: INestedData[] = fetchedData.map((item, index) => ({
          ...item,
          key: index,
          level: 1,
          hasFetchedChildren: false,
          children: [],
        }));
        setTemporaryData(shapedData);
      } catch (error) {
        setTemporaryData([]);
        console.error("Error fetching performance data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTableData();
  }, [
    selectedTab,
    dateRange,
    canvasApi,
    asin,
    saturationKeys.level1,
    saturation,
  ]);

  // Recursively find nested records by their key path
  const findRecordByKey = useCallback((data: any[], key: string): any => {
    for (const item of data) {
      if (item.key.toString() === key) return item;
      if (item.children?.length) {
        const found = findRecordByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const updateAllExpandedRows = useCallback(async () => {
    if (!temporaryData) return;
    setIsLoading(true);
    try {
      const fetchTop = fetcherMap[selectedTab];
      if (!fetchTop) return;
      const topRaw = await fetchTop(
        from,
        to,
        saturation,
        asin,
        saturationKeys.level1,
      );
      let updatedData = topRaw.map((item, idx) => ({
        ...item,
        key: idx,
        level: 1,
        hasFetchedChildren: false,
        children: [],
      }));
      const shapeChildren = (raw: any[], parent: INestedData) =>
        raw.map((child, i) => ({
          ...child,
          key: `${parent.key}-${i}`,
          level: parent.level + 1,
          hasFetchedChildren: false,
          performance: child.targeting_text || child.campaign_name,
          campaign_name: child.search_term,
          ad_type: child.search_term || child.campaign,
          match_type: child.target || child.campaign_name,
          match_type_key: child.match_type,
          performanceKey: child.performance,
          ad_typeKey: parent.ad_type,
          funnelKey: parent.funnel,
          funnel: child.campaign_name || child.funnel,
          children: [],
        }));
      const fetchAndInsert = async (record: INestedData) => {
        const levelMap = record.level === 1 ? level1Map : level2Map;
        const getData = levelMap[selectedTab];
        if (!getData) return;
        const keys = saturationKeys[`level${record.level + 1}`];
        const rawChildren = await getData(
          from,
          to,
          record,
          saturation,
          asin,
          keys,
        );
        const children = shapeChildren(rawChildren, record);
        setExpandedRowsData((prev) => ({ ...prev, [record.key]: children }));
        updatedData = updateRecordChildren(updatedData, record.key, children);
      };
      const records = expandedRowKeys
        .map((key) => {
          const parts = key.toString().split("-");
          return parts.length === 1
            ? updatedData.find((r) => r.key === Number(parts[0]))
            : findRecordByKey(updatedData, key.toString());
        })
        .filter((r): r is INestedData => Boolean(r));
      for (const record of records) await fetchAndInsert(record);
      setTemporaryData(updatedData);
    } catch (e) {
      console.error("Error updating expanded rows on saturation change:", e);
    } finally {
      setIsLoading(false);
    }
  }, [
    temporaryData,
    expandedRowKeys,
    selectedTab,
    saturation,
    from,
    to,
    asin,
    saturationKeys,
  ]);

  // Update data when saturation changes but preserve expanded state
  function syncPreviousSaturation() {
    if (previousSaturation !== saturation && expandedRowKeys.length > 0) {
      updateAllExpandedRows();
    }
    setPreviousSaturation(saturation);
  }
  useEffect(syncPreviousSaturation, [
    saturation,
    previousSaturation,
    expandedRowKeys,
    updateAllExpandedRows,
  ]);

  // ---- Use extracted updateSelectedNestedRecords ----
  useEffect(() => {
    handleUpdateSelectedNestedRecords();
  }, [
    saturation,
    saturationKeys,
    expandedRowKeys,
    tableKeys,
    tableData,
    from,
    to,
    selectedTab,
    asin,
    handleUpdateSelectedNestedRecords,
  ]);

  const mergeResults = (acc: TableItem[], curr: TableItem[]) =>
    curr.map((item) => {
      const existing = acc.find((i) => i.key === item.key);
      return existing || item;
    });

  // ---- Use extracted prepareTableData ----
  useEffect(() => {
    prepareTableData();
  }, [prepareTableData]);

  // Reset keys and saturation when tab changes
  useEffect(() => {
    setExpandedRowKeys([]);
    setExpandedRowsData({});
    setSaturationKeys({ level1: [], level2: [], level3: [] });
    setTableKeys([]);
  }, [selectedTab]);

  // Handle row selection changes (checkbox in first column)
  const onSelectChange = async (
    newSelectedRowKeys: string[],
    selectedRows: any[],
  ) => {
    setTableKeys(newSelectedRowKeys);

    const keysByLevel: {
      level1: Set<string>;
      level2: Set<string>;
      level3: Set<string>;
    } = {
      level1: new Set(),
      level2: new Set(),
      level3: new Set(),
    };

    selectedRows.forEach((row) => {
      const level = `level${row.level}` as keyof typeof keysByLevel;
      const key = getSaturationKey(row, selectedTab);
      if (key && keysByLevel[level]) {
        keysByLevel[level].add(key);
      }
    });

    const newSaturationKeys = {
      level1: Array.from(keysByLevel.level1),
      level2: Array.from(keysByLevel.level2),
      level3: Array.from(keysByLevel.level3),
    };
    setSaturationKeys(newSaturationKeys);

    if (temporaryData) {
      const baseData = [...temporaryData];
      for (const row of selectedRows) {
        const keys = Array.from(keysByLevel[`level${row.level}`]);
        try {
          setIsLoading(true);
          const updatedRecords = await updateNestedRecord(row, baseData, {
            from,
            to,
            selectedTab,
            saturation,
            asins: asin,
            saturationKeys: keys,
            expandedKeys: expandedRowKeys as string[],
          });
          setTemporaryData(updatedRecords);
        } catch (error) {
          console.error("Error updating selected record:", error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    handleRestoreExpandedRows();
  }, [handleRestoreExpandedRows]);

  const handleExpand = async (expanded: boolean, record: any) => {
    if (!expanded) {
      setExpandedRowKeys((prev) => prev.filter((k) => k !== record.key));
      return;
    }

    setExpandedRowKeys((prev) => [...prev, record.key]);

    try {
      setIsLoading(true);
      const getData =
        Number(record.level) === 1
          ? level1Map[selectedTab]
          : level2Map[selectedTab];
      if (!getData) return;

      const keys = saturationKeys[`level${Number(record.level) + 1}`] || [];

      const childrenData = await getData(
        from,
        to,
        record,
        saturation,
        asin,
        keys,
      );

      const shapedChildren = childrenData.map((child: any, index: number) => ({
        ...child,
        key: `${record.key}-${index}`,
        level: Number(record.level) + 1,
        hasFetchedChildren: false,
        performance: child.targeting_text || child.campaign_name,
        campaign_name: child.search_term,
        ad_type: child.search_term || child.campaign,
        target: child.campaign_name,
        match_type: child.target || child.campaign_name,
        match_type_key: child.match_type,
        performanceKey: child.performance,
        ad_typeKey: record.ad_type,
        funnelKey: record.funnel,
        funnel: child.campaign_name || child.funnel,
        children: [],
      }));

      setExpandedRowsData((prev) => ({
        ...prev,
        [record.key]: shapedChildren,
      }));

      setTemporaryData(
        (prev) =>
          prev && updateRecordChildren(prev, record.key, shapedChildren),
      );
    } catch (error) {
      console.error("Error fetching children data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const rowSelection = {
    tableKeys,
    onChange: onSelectChange,
    onSelect: async (record: any, selected: boolean) => {
      if (selected && Number(record.level) > 0) {
        try {
          setIsLoading(true);
          const keys = saturationKeys[`level${Number(record.level)}`] || [];
          const updatedData = await updateNestedRecord(record, tableData, {
            from,
            to,
            selectedTab,
            saturation,
            asins: asin,
            saturationKeys: keys,
          });
          setTemporaryData(updatedData);
        } catch (error) {
          console.error("Error updating selected record:", error);
        } finally {
          setIsLoading(false);
        }
      }
    },
  };
  if (!adCampaignCampaignTableEnabled) {
    return null;
  }
  return (
    <>
      <div className={styles.header}>
        <TabsTypeList
          isFunnelChart
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        <ColumnsSettingsModal
          availableColumns={getAllAvailableColumns(selectedTab)}
          selectedColumns={selectedColumns}
          onChange={setSelectedColumns}
          getDefaultColumns={() => getDefaultColumns(selectedTab)}
          selectedTab={selectedTab}
          className={styles.columnsSettingsModal}
          isWhatIfEnabled={isWhatIf}
        />
      </div>
      <div
        className={classNames(styles.table, {
          [styles.autoHeight]: autoHeight,
        })}
        ref={tableRef}
      >
        <ExpandableTable
          columns={getFilteredColumns()}
          data={tableData}
          rowSelection={rowSelection}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              void handleExpand(expanded, record);
            },
            rowExpandable: () => false,
          }}
          scroll={{ x: "max-content", y: `${autoHeight ? height : 400}px` }}
          adSalesTab={selectedTab}
          isLoading={isLoading}
        />
      </div>
    </>
  );
};
